import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Shield, Menu, X, PhoneCall, Facebook } from "lucide-react";

const NAV = [
  { to: "/", label: "Home", testId: "nav-home-link" },
  { to: "/services", label: "Global Service Index", testId: "nav-services-link" },
  { to: "/initiatives", label: "Initiatives", testId: "nav-initiatives-link" },
  { to: "/achievements", label: "Achievements", testId: "nav-achievements-link" },
  { to: "/contact", label: "Contact & Support", testId: "nav-contact-link" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#0A0D14] text-slate-100 flex flex-col">
      <header className="sticky top-0 z-50 bg-[#0A0D14]/85 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-3 group">
            <img
              src="/assets/pain-ribbon-logo.webp"
              alt="P.A.I.N. ribbon logo"
              className="h-9 w-9 rounded-md border border-amber-500/40 object-cover group-hover:shadow-[0_0_18px_-4px_rgba(29,78,216,0.7)] transition-shadow"
            />
            <span className="leading-tight">
              <span className="block font-display font-bold tracking-wide text-sm sm:text-base text-slate-50">
                P.A.I.N.
              </span>
              <span className="block text-[10px] uppercase tracking-[0.18em] text-slate-400">
                People Against Intentional Neglect
              </span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="Primary">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testId}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                    isActive
                      ? "text-amber-400 bg-amber-500/10"
                      : "text-slate-300 hover:text-slate-50 hover:bg-white/5"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-emergency-button"
              className="btn-royal ml-3 inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white"
            >
              <PhoneCall className="h-4 w-4" />
              Get Help Now
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-md text-slate-300 hover:bg-white/5"
            onClick={() => setOpen(!open)}
            aria-label="Toggle navigation menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <nav className="lg:hidden border-t border-white/10 bg-[#0A0D14]/95 backdrop-blur-xl" aria-label="Mobile">
            <div className="px-4 py-3 space-y-1">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-${item.testId}`}
                  className={({ isActive }) =>
                    `block px-3 py-2.5 rounded-md text-sm font-medium ${
                      isActive ? "text-amber-400 bg-amber-500/10" : "text-slate-300 hover:bg-white/5"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-emergency-button"
                className="btn-royal mt-2 flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold text-white"
              >
                <PhoneCall className="h-4 w-4" />
                Get Help Now
              </Link>
            </div>
          </nav>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-white/10 bg-[#080B11]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid gap-10 md:grid-cols-3">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img
                src="/assets/pain-ribbon-logo.webp"
                alt="P.A.I.N. ribbon logo"
                className="h-9 w-9 rounded-md border border-amber-500/40 object-cover"
              />
              <span className="font-display font-bold text-slate-50">P.A.I.N.</span>
            </div>
            <p className="text-sm text-slate-400 leading-relaxed">
              People Against Intentional Neglect — a registered Australian charity founded by Ronald
              Waters, dedicated to child abuse prevention and breaking decades of institutional
              silence.
            </p>
            <a
              href="https://www.facebook.com/PAINAus"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-facebook-link"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-amber-400 hover:text-amber-300 transition-colors"
            >
              <Facebook className="h-4 w-4" />
              Follow P.A.I.N. on Facebook
            </a>
          </div>
          <div>
            <h3 className="font-code text-xs uppercase tracking-[0.2em] text-amber-400/90 mb-4">Navigate</h3>
            <ul className="space-y-2 text-sm">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    data-testid={`footer-${item.testId}`}
                    className="text-slate-400 hover:text-amber-400 transition-colors"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-code text-xs uppercase tracking-[0.2em] text-amber-400/90 mb-4">
              Crisis Support (Australia)
            </h3>
            <ul className="space-y-2 text-sm text-slate-400">
              <li>Emergency: <span className="text-slate-100 font-semibold">000</span></li>
              <li>Lifeline: <span className="text-slate-100 font-semibold">13 11 14</span></li>
              <li>Kids Helpline: <span className="text-slate-100 font-semibold">1800 55 1800</span></li>
            </ul>
            <h3 className="font-code text-xs uppercase tracking-[0.2em] text-amber-400/90 mt-6 mb-3">
              Allied Services
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href="https://bem-crisis.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-bem-link"
                  className="text-slate-400 hover:text-amber-400 transition-colors"
                >
                  Behind Every Mask — BEM Crisis App
                </a>
              </li>
              <li>
                <a
                  href="mailto:enquiries@harmonyharbour.co.uk"
                  data-testid="footer-harmony-link"
                  className="text-slate-400 hover:text-amber-400 transition-colors"
                >
                  Harmony Harbour UK — 24/7 Mental Health
                </a>
              </li>
              <li className="text-slate-400">
                Harmony 24/7 Crisis: <a href="tel:08001613122" className="text-slate-100 font-semibold">0800 161 3122</a>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-xs text-slate-500">
                &copy; {new Date().getFullYear()} People Against Intentional Neglect (P.A.I.N.) — Registered
                Australian Charity. "They did not know we were seeds to be grown."
              </p>
              <span
                data-testid="footer-acnc-badge"
                className="mt-2 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full border border-amber-500/40 bg-white/95 text-slate-800 text-[10px] font-code uppercase tracking-wider font-semibold"
              >
                <img
                  src="/assets/acnc-tick.jpg"
                  alt="ACNC Registered Charity tick"
                  className="h-5 w-5 rounded-full object-cover"
                />
                ACNC Registered Charity — ABN 95 161 193 853
              </span>
            </div>
            <Link
              to="/admin"
              data-testid="footer-admin-link"
              className="text-xs text-slate-600 hover:text-amber-400 transition-colors"
            >
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
